import React from 'react';
import { Box, Chip, Paper, Stack, Typography } from '@mui/material';
import { AlertCircle } from 'lucide-react';
import { BankSearchPool } from '../../../../utils/types/candi_bank_types';

export interface CoverageBannerProps {
  pool: BankSearchPool | null;
  skillMatchesStale: boolean;
}

export const CoverageBanner: React.FC<CoverageBannerProps> = ({
  pool,
  skillMatchesStale,
}) => {
  if (!pool) return null;

  const noMandatory = !pool.activeComponents?.includes('mandatory');

  return (
    <Paper
      variant='outlined'
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1.5,
        bgcolor: 'rgba(255, 255, 255, 0.85)',
        backdropFilter: 'blur(8px)',
        borderColor: 'divider',
        borderRadius: 3,
        px: 2,
        py: 1.25,
      }}
    >
      {noMandatory ? (
        <Stack direction='row' spacing={1} alignItems='center' sx={{ color: '#b45309' }}>
          <AlertCircle size={16} color='#d97706' />
          <Typography variant='caption' fontWeight={600} color='inherit'>
            None of the listed skills appear in your pool — ranking by overall profile similarity only.
          </Typography>
        </Stack>
      ) : (
        <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap'>
          <Typography
            variant='caption'
            fontWeight={700}
            color='text.secondary'
            textTransform='uppercase'
            letterSpacing='0.05em'
          >
            Pool Coverage:
          </Typography>
          {pool.coverage &&
            Object.entries(pool.coverage).map(([skill, count]) => {
              const hasMatches = count > 0;
              return (
                <Chip
                  key={skill}
                  size='small'
                  variant='outlined'
                  label={`${skill} (${count})`}
                  sx={{
                    fontSize: '0.6875rem',
                    fontWeight: 700,
                    height: 22,
                    bgcolor: hasMatches ? 'rgba(16, 185, 129, 0.08)' : 'rgba(239, 68, 68, 0.08)',
                    borderColor: hasMatches ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)',
                    color: hasMatches ? '#047857' : '#b91c1c',
                  }}
                />
              );
            })}
        </Stack>
      )}

      {skillMatchesStale && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: 'primary.main',
            ml: 'auto',
          }}
        >
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              bgcolor: 'primary.main',
            }}
          />
          <Typography variant='caption' fontWeight={700} color='inherit'>
            Pool updated — refining matches…
          </Typography>
        </Box>
      )}
    </Paper>
  );
};
